// 📐 **세이프존 SSOT** — 플랫폼 UI 에 가려지거나 잘리는 자리를 한 곳에서 정한다.
//
// ★왜 전역 한 벌인가★(회장님 2026-09-23) — 지금까지 «안전 영역»이 표면마다 따로 있었다:
//   장표는 `LAYOUT[aspect].safe`, 생성형 글자 자리는 «아래 20%» 리터럴, 자막은 또 자기 값.
//   그 값들이 **실제로 잘리는 자리보다 한참 넓어서** 글자가 UI 에 먹혔다. 이 파일이 그 정본이다.
//
// ★출처 = 실측★ — 회장님이 지정한 데드존 가이드 영상(뮤팟 「요즘 유튜버들이 꼭 지키는 데드존 가이드」,
//   1920×1080 · 39.4s)의 템플릿 프레임을 픽셀로 쟀다. 두 프레임(t=3s 의 3종 비교판, t=34s 의 단독판)에서
//   **같은 값**이 나왔다(세로 B: 6.77/21.09/73.18/88.54 · 좌 5.53 · 우 14.75 — 소수 둘째 자리까지 일치).
//   ⇒ 값을 고칠 때는 이 파일의 주석과 `tests/unit/safe-zone.test.ts` 의 박제를 **같이** 고쳐라.
//
// ★층이 셋이다★ — «안전/위험» 두 값으로는 이 가이드를 담을 수 없다:
//   ① `ui`    — 플랫폼 UI 가 실제로 덮는 바깥 띠. **아무것도 두지 않는다.**
//   ② `title` — 그 안쪽의 빗금 구역. **제목·로고처럼 «잘려도 뜻이 사는 것»만** 둔다.
//   ③ `core`  — 완전 안전. 본문·숫자·읽어야 하는 글자는 전부 여기.
//   (가이드가 빗금을 «타이틀 혹은 로고 안전 구역»으로 이름 붙인 그대로다.)

/** 이 SSOT 가 값을 가진 화면비. 장표 `LAYOUT` 과 같은 표기를 쓴다. */
export type SafeAspect = "16:9" | "9:16" | "4:5";

/** 네 변에서 안쪽으로 들어간 비율(0~1). 좌우가 **다를 수 있다** — 세로 화면은 오른쪽에 버튼 열이 선다. */
export interface SafeInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface SafeZone {
  /** ① UI 가 덮는 바깥 띠 — 아무것도 두지 않는다. */
  ui: SafeInset;
  /** ③ 완전 안전 — 본문·숫자·읽어야 하는 글자. `ui` 와의 사이가 ② 빗금(제목·로고) 구역이다. */
  core: SafeInset;
}

/**
 * 📐 실측 표. 세로판은 **오른쪽이 왼쪽의 2.7배**다(좋아요·댓글·공유 버튼 열).
 *
 * ⚠️ `4:5` 는 **가이드에 없다** — 영상이 다룬 것은 세로 9:16 과 가로 16:9 둘뿐이다. 그래서 **9:16 실측에서
 *    기하로 옮겨 온다**(2026-09-23 재파생): 4:5 영상은 세로 전체화면(릴스·쇼츠)에서 **폭에 맞춰 가운데에 뜨고
 *    위아래가 검은 띠**가 된다(1080×1920 화면에 1080×1350 영상 → 띠 285px 씩). 버튼·설명글은 **화면에** 붙어
 *    있으므로 9:16 의 화면 픽셀 경계를 영상 좌표로 옮기면 된다: `(9:16 비율 × 1920 − 285) ÷ 1350`, 음수면 0
 *    (그 UI 는 검은 띠 위에 떨어진다). 좌우는 폭이 같아 9:16 값 그대로다(오른쪽 버튼 열 14.75%).
 *    ⇒ core 세로 높이가 9:16 과 **같은 1,000px** 이 된다(같은 화면이니 당연하다).
 *    ★종전 파생(09-23 오전)은 틀렸다★ — 세로 «비율»을 그대로 복사해 안전 높이가 703px 로 쪼그라들었고
 *    (장표 4종이 최대 60% 넘침 · 렌더 실측), 좌우는 가로판(4.17%)을 따라 오른쪽 버튼 열을 오히려 덜 피했다.
 *    피드(버튼이 영상 밖)에서 보면 이 값은 넉넉한 쪽이라 손해가 없다. 실측할 기회가 오면 갈아끼워라.
 */
export const SAFE_ZONE: Readonly<Record<SafeAspect, SafeZone>> = {
  // 세로 — 데드존 가이드 «세로 B»(릴스·쇼츠) 실측.
  //   상단 UI 6.77% · 빗금 6.77~21.09% · 안전 21.09~73.18% · 빗금 73.18~88.54% · 하단 UI 11.46%
  //   좌 UI 5.53% · 우 UI 14.75%
  "9:16": {
    ui: { top: 0.0677, bottom: 0.1146, left: 0.0553, right: 0.1475 },
    core: { top: 0.2109, bottom: 0.2682, left: 0.0553, right: 0.1475 },
  },
  // 가로 — 같은 가이드의 16:9 판 실측. 상단 UI 12.73% · 안전 12.73~82.87% · 빗금 82.87~89.35% · 하단 UI 10.65%.
  //   ★좌우는 가이드가 띠를 두지 않았다★(전폭). 그렇다고 0 으로 두면 글자가 모서리에 붙으므로
  //   종전 장표 값(80/1920 = 4.17%)을 **여백의 하한**으로 유지한다 — 실측이 «더 좁혀도 된다»고 한 것이지
  //   «붙여도 된다»고 한 것이 아니다.
  "16:9": {
    ui: { top: 0.1273, bottom: 0.1065, left: 0.0417, right: 0.0417 },
    core: { top: 0.1273, bottom: 0.1713, left: 0.0417, right: 0.0417 },
  },
  // 4:5 — ★파생값(미실측)★ = 9:16 실측을 «세로 화면 가운데 띄움» 기하로 옮긴 값. 위 주석 참조.
  //   core top (0.2109×1920 − 285)/1350 = 0.0888 · bottom (0.2682×1920 − 285)/1350 = 0.1703
  //   ui top·bottom 은 계산값이 음수(UI 가 검은 띠 위) → 0.
  "4:5": {
    ui: { top: 0, bottom: 0, left: 0.0553, right: 0.1475 },
    core: { top: 0.0888, bottom: 0.1703, left: 0.0553, right: 0.1475 },
  },
} as const;

/** 어느 층을 쓸 것인가. 기본은 `core`(읽어야 하는 글자). */
export type SafeLevel = "ui" | "core";

export const safeInset = (aspect: SafeAspect, level: SafeLevel = "core"): SafeInset =>
  SAFE_ZONE[aspect][level];

/**
 * 📐 **프레임 크기 → 가장 가까운 세이프존 화면비**. 표에 없는 화면비(1:1 · 3:4 · 4:3)도 글자를 그리는 표면이
 * 있어서(번인 자막은 화면비 무관) 가장 가까운 실측·파생값을 빌린다: 1:1·3:4 → 4:5, 4:3 → 16:9.
 * 무효 프레임은 9:16(주력 · 가장 좁은 쪽)으로 접는다.
 */
export function nearestSafeAspect(width: number, height: number): SafeAspect {
  if (!(width > 0 && height > 0 && Number.isFinite(width) && Number.isFinite(height))) return "9:16";
  const r = width / height;
  const table: ReadonlyArray<[SafeAspect, number]> = [["9:16", 9 / 16], ["4:5", 4 / 5], ["16:9", 16 / 9]];
  let best = table[0];
  for (const t of table) if (Math.abs(r - t[1]) < Math.abs(r - best[1])) best = t;
  return best[0];
}

/**
 * 📐 **가운데 정렬 글자가 쓸 수 있는 폭**(프레임 폭 대비) = `1 − 2 × max(좌, 우)`.
 * ★가운데를 지키려면 넓은 쪽 여백을 양쪽에 다 준다★ — 세로 화면은 오른쪽 버튼 열(14.75%)이 넓어서
 *   왼쪽(5.53%)에 맞추면 글자 줄 오른쪽 끝이 버튼 밑으로 들어간다. 회장님 09-23 자막 «가안»(가운데 유지 ·
 *   한 줄이 짧아지는 쪽)의 정의다. 한쪽으로 치우쳐도 되는 요소는 `safeInset` 의 좌우를 직접 쓴다.
 */
export function centeredSafeWidth(aspect: SafeAspect, level: SafeLevel = "core"): number {
  const s = safeInset(aspect, level);
  return Math.max(0, 1 - 2 * Math.max(s.left, s.right));
}

/**
 * 📐 **상자 자막(캔버스 번인 `drawCaption`)의 자리** — 위 = max(5%, core 상단) · 아래 = max(7%, core 하단) ·
 * 폭 상한 = min(90%, 가운데 정렬 core 안전 폭). ★렌더와 미리보기(`FlowClipPlayer`)가 같은 값을 읽는다★(세이프존 3-b) —
 * 종전 미리보기는 위 1rem·아래 1.5rem·폭 90% 로 따로 그려, 번인 자리와 갈렸다.
 */
export function captionBoxFrameFrac(aspect: SafeAspect): { top: number; bottom: number; maxWidth: number } {
  const core = safeInset(aspect, "core");
  return {
    top: Math.max(0.05, core.top),
    bottom: Math.max(0.07, core.bottom),
    maxWidth: Math.min(0.9, centeredSafeWidth(aspect)),
  };
}

/** 실해상도 픽셀로 환산. 위·아래는 높이, 좌·우는 폭 기준이다(비율 축을 섞지 마라). */
export function safeInsetPx(
  aspect: SafeAspect,
  width: number,
  height: number,
  level: SafeLevel = "core",
): { top: number; bottom: number; left: number; right: number } {
  const s = safeInset(aspect, level);
  return {
    top: Math.round(height * s.top),
    bottom: Math.round(height * s.bottom),
    left: Math.round(width * s.left),
    right: Math.round(width * s.right),
  };
}

/** 정규화 사각형(좌상단 기준 0~1). 글자 자리·오버레이가 쓰는 표기와 같다. */
export interface SafeRect { x: number; y: number; w: number; h: number }

/** 그 층 안에 **완전히** 들어오는가. 경계에 걸치면 거짓이다. */
export function isInsideSafe(rect: SafeRect, aspect: SafeAspect, level: SafeLevel = "core"): boolean {
  const s = safeInset(aspect, level);
  const eps = 1e-6;
  return (
    rect.x >= s.left - eps &&
    rect.y >= s.top - eps &&
    rect.x + rect.w <= 1 - s.right + eps &&
    rect.y + rect.h <= 1 - s.bottom + eps
  );
}

/**
 * 그 층 안으로 밀어 넣는다 — **크기를 먼저 줄이지 않고 자리부터 옮긴다**(글자 크기가 조용히 바뀌면
 * 유저가 맞춰 둔 배치가 흔들린다). 그래도 안 들어가면 그때만 줄인다.
 */
export function clampRectToSafe(rect: SafeRect, aspect: SafeAspect, level: SafeLevel = "core"): SafeRect {
  const s = safeInset(aspect, level);
  const maxW = Math.max(0, 1 - s.left - s.right);
  const maxH = Math.max(0, 1 - s.top - s.bottom);
  const w = Math.min(rect.w, maxW);
  const h = Math.min(rect.h, maxH);
  const x = Math.min(Math.max(rect.x, s.left), 1 - s.right - w);
  const y = Math.min(Math.max(rect.y, s.top), 1 - s.bottom - h);
  return { x, y, w, h };
}
